<script setup lang="ts">
import type Spectrum from '@/core/resources/Spectrum'
import GenericTable from '@/components/GenericTable.vue'
import SpectraTableLine from '@/components/tables/SpectraTableLine.vue'
import PaginationMenu from '@/components/tables/PaginationMenu.vue'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { spectraService } from '@/core/services'
import { useStore } from '@/core/store'
import { handleError } from '@/utils/ErrorHandler'
import { VisibilitySpectrum } from '@/core/Common'
import { popupUtil } from '@/utils/Popup'

const fetchSpectra = async (force?: boolean) => {
  return await spectraService
    .getAllPaginated(
      store.getAccessToken(),
      pagination.value.itemsPerPage,
      pagination.value.page,
      sortParams.value.key,
      sortParams.value.desc,
      search.value,
      exactSearch.value
    )
    .then((res) => {
      if (selectedSpectra().length == 0 || force) {
        spectraList.value = res.results
        pagination.value.itemsLength = res.count
        pagination.value.pageCount = res.num_pages
        pagination.value.pageStart = res.start_index - 1
        pagination.value.pageStop = res.end_index

        selectedMap.value = {}
        res.results.forEach((s) => {
          selectedMap.value[s.calc_key] = false
        })
        allSelected.value = false
      }

      return true
    })
    .catch((err) => {
      handleError(err)
      return false
    })
}

const deleteSpectra = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to delete the selected spectra?')
  if (!confirmed) return

  spectraService
    .deleteMany(selectedSpectra(), store.getAccessToken())
    .then(() => {
      fetchSpectra(true)
    })
    .catch(handleError)
}

const selectedSpectra = () => {
  return spectraList.value.filter((s) => selectedMap.value[s.calc_key])
}

const updateSpectraVisibility = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to change the visibility of the selected spectra?')
  if (!confirmed) {
    selectedVisibility.value = ''
    return
  }

  spectraService
    .updateSpectraVisibility(
      selectedSpectra().map((s) => s.calc_key),
      VisibilitySpectrum[selectedVisibility.value as keyof typeof VisibilitySpectrum],
      store.getAccessToken()
    )
    .then(() => {
      fetchSpectra(true)
    })
    .catch(handleError)

  selectedVisibility.value = ''
}

const handleSearch = () => {
  pagination.value.page = 1
  fetchSpectra(true)
}

let interval: any
const allSelected = ref(false)
const store = useStore()
const spectraList = ref<Spectrum[]>([])
const selectedMap = ref<{ [key: string]: boolean }>({})
const search = ref('')
const exactSearch = ref(false)
const selectedVisibility = ref('')
const sortParams = ref({ key: '', desc: false })
const headers = [
  { key: 'hidden', label: 'Visibility' },
  { key: 'short_name', label: 'Short Name' },
  { key: 'collection', label: 'Collection' },
  { key: 'version', label: 'Version' }
]
const pagination = ref({
  itemsLength: 0,
  itemsPerPage: 20,
  pageCount: 0,
  page: 1,
  pageStart: 0,
  pageStop: 0
})

const intervalFunction = async () => {
  if (selectedSpectra().length != 0) {
    interval = setTimeout(intervalFunction, 1000)
  } else {
    const success = await fetchSpectra()
    if (success) interval = setTimeout(intervalFunction, 1000)
  }
}

onMounted(() => {
  intervalFunction()
})

onBeforeUnmount(() => {
  clearInterval(interval)
})

watch(allSelected, (value) => {
  for (const key in selectedMap.value) {
    selectedMap.value[key] = value
  }
})

watch(exactSearch, () => {
  handleSearch()
})
</script>

<template>
  <div class="container">
    <h1>List of spectra</h1>
    <div class="toolbar">
      <router-link to="/spectra/import" class="button" id="import-spectra">
        <span class="material-symbols-outlined">add</span>
        <span>Import spectra</span>
      </router-link>

      <button @click="fetchSpectra(true)" id="refresh-spectra">
        <span class="material-symbols-outlined">refresh</span>
        <span>Refresh</span>
      </button>
      <button :disabled="selectedSpectra().length === 0" @click="deleteSpectra" id="delete-spectra">
        <span class="material-symbols-outlined">delete</span>
        <span>Delete</span>
      </button>

      <span> Change visibility: </span>

      <select
        :disabled="selectedSpectra().length === 0"
        v-model="selectedVisibility"
        @change="updateSpectraVisibility"
        id="visibility-spectra">
        <option disabled value="">Select visibility</option>
        <option value="DISPLAY">Published</option>
        <option value="READY">Ready to publish</option>
        <option value="HIDDEN">Hidden</option>
      </select>

      <div class="search-bar">
        <span class="material-symbols-outlined search-icon">search</span>
        <input type="text" v-model="search" placeholder="Search..." @keyup="handleSearch" />
        <button @click="exactSearch = !exactSearch" id="exact-search">
          <span v-if="exactSearch" class="material-symbols-outlined">equal</span>
          <span v-else class="material-symbols-outlined">title</span>
          <div class="tooltip">
            <span v-if="exactSearch">Exact search</span>
            <span v-else>Including search</span>
          </div>
        </button>
      </div>
    </div>

    <GenericTable :headers="headers" v-model:sortParams="sortParams" v-model:checked="allSelected" @fetch="fetchSpectra">
      <SpectraTableLine
        v-for="spectrum in spectraList"
        :key="spectrum.calc_key"
        :spectrum="spectrum"
        v-model="selectedMap[spectrum.calc_key]" />
    </GenericTable>

    <PaginationMenu
      v-model:page="pagination.page"
      v-model:items-per-page="pagination.itemsPerPage"
      :pagination="pagination"
      @fetch="fetchSpectra"
      :optionValues="[5, 20, 50, 75, 100, 1000, 5000]" />
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/styles/table.scss';

#exact-search {
  position: relative;
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  color: var(--theme-text);

  &:hover .tooltip {
    opacity: 1;
  }
}

.tooltip {
  text-transform: none;
  pointer-events: none;
  display: block;
  position: absolute;
  width: max-content;
  top: -1.5rem;
  background-color: var(--theme-panel);
  color: var(--theme-text);
  z-index: 100;
  font-size: 1rem;
  opacity: 0;
  transition: opacity 0.2s;
}
</style>
